// components/add_portfolio_manager.tsx
import React, { useState, useContext, useCallback } from 'react';
import { TextInput, Button, Alert } from '@mantine/core';
import { DocumentReference, updateDoc, Timestamp, arrayUnion } from 'firebase/firestore';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { MyUserDataContext } from '../contexts';
import { Service, UpdatedDataType } from '../types';
import { GenevaApiSelect } from './GenevaApiSelect';

interface AddPortfolioManagerProps {
    serviceRef: DocumentReference;
    service: Service;
}

interface ValidationErrors {
    portfolioManager?: string;
    reason?: string;
}

// Validation constants
const VALIDATION_RULES = {
    MIN_PORTFOLIO_ID_LENGTH: 3,
    MIN_REASON_LENGTH: 10,
} as const;

const AddPortfolioManager: React.FC<AddPortfolioManagerProps> = ({ serviceRef, service }) => {
    const [updating, setUpdating] = useState(false);
    const [portfolioManager, setPortfolioManager] = useState(service.portfolioOpeningRequest?.portfolioManager);
    const [reason, setReason] = useState('');
    const [errors, setErrors] = useState<ValidationErrors>({});

    const userData = useContext(MyUserDataContext);

    // Validation helpers
    const validatePortfolioManager = useCallback((value: string | undefined): string | undefined => {
        const trimmed = value?.trim();
        if (!trimmed) return "Portfolio Manager is required";

        return undefined;
    }, []);



    const validateForm = useCallback((): boolean => {
        const portfolioManagerError = validatePortfolioManager(portfolioManager);


        const newErrors: ValidationErrors = {
            portfolioManager: portfolioManagerError,

        };

        setErrors(newErrors);
        return !portfolioManagerError;
    }, [portfolioManager, validatePortfolioManager,

    ]);

    const isFormValid = useCallback((): boolean => {
        return !!portfolioManager?.length;
        // Removed !updating from here
    }, [portfolioManager]); // Removed updating from dependencies






    const handleSubmit = useCallback(async () => {
        if (!validateForm()) return;

        setUpdating(true);

        try {
            const newEntry: UpdatedDataType = {
                updated_by: userData.user?.email || "unknown",
                updated_on: Timestamp.now(),
                updated_field_name: ["portfolioManager"],
                updated_fields: [{ portfolioManager: portfolioManager?.trim() || "" }],
                initial_value: [{ portfolioManager: service.portfolioOpeningRequest?.portfolioManager || "" }],
                reason: reason.trim()
            };

            const portfolioOpeningRequest = {
                ...service.portfolioOpeningRequest,
                portfolioManager: portfolioManager?.trim()
            };

            await updateDoc(serviceRef, {
                portfolioOpeningRequest,
                updated_data: arrayUnion(newEntry)
            });

            // Show success notification
            notifications.show({
                title: 'Success!',
                message: 'Portfolio Manager updated successfully',
                color: 'green',
                icon: <IconCheck size={16} />
            });

            // Reset form and close modal
            setPortfolioManager(undefined);
            setReason('');
            setErrors({});
            modals.closeAll();

        } catch (error) {
            console.error("Error updating portfolio Manager:", error);

            // Show error notification instead of alert
            const message = error instanceof Error ? error.message : 'Unknown error occurred';
            notifications.show({
                title: 'Error',
                message: `Failed to update portfolio Manager: ${message}`,
                color: 'red',
                icon: <IconAlertCircle size={16} />
            });
        } finally {
            setUpdating(false);
        }
    }, [validateForm, userData.user?.email, portfolioManager, reason, service.portfolioOpeningRequest, serviceRef]);

    // Derived state
    const hasErrors = Boolean(errors.portfolioManager || errors.reason);
    const formStatus = isFormValid();

    return (
        <>


            <GenevaApiSelect
                key={"Manager"}
                linkTable={"Manager"}
                label={"Manager"}
                value={portfolioManager}

                onChange={(value) => { value ? setPortfolioManager(value) : setPortfolioManager(undefined); }}
                // {...form.getInputProps("service.portfolioOpeningRequest.portfolioManager")}
                onSelectItem={() => null}
                disabled={false}
                // required={true}
                placeholder={"Select Portfolio Manager"}
                showApiLabel={true}
            />
            <Button
                loading={updating}
                fullWidth
                disabled={updating}
                onClick={handleSubmit}
                mt="md"
            >
                Submit
            </Button>

            {/* Enhanced validation summary */}
            {hasErrors && (
                <Alert
                    icon={<IconAlertCircle size={16} />}
                    title="Please fix the following errors:"
                    color="red"
                    mt="sm"
                >
                    {errors.portfolioManager && <div>• {errors.portfolioManager}</div>}
                    {errors.reason && <div>• {errors.reason}</div>}
                </Alert>
            )}

            {/* Form status indicator - don't show during loading */}
            {!updating && (
                <Alert
                    color={formStatus ? 'green' : 'gray'}
                    variant="light"
                    mt="xs"
                    style={{ fontSize: '12px' }}
                >
                    Form is {formStatus ? 'ready to submit' : 'incomplete'}
                </Alert>
            )}
        </>
    );
};

export default AddPortfolioManager;